"use client";

import RouteError from "@/components/RouteError";

/**
 * Root-level error boundary (Next.js App Router `global-error.tsx`).
 *
 * This is the ONLY boundary that catches a crash in the root layout/template
 * itself — a per-route `error.tsx` (see `RouteError`) cannot, because it renders
 * *inside* the root layout that already failed. It replaces the whole document,
 * so it must emit its own `<html>`/`<body>`.
 *
 * Why this matters here: server-side Sentry is intentionally OFF on the
 * Cloudflare workerd runtime (`sentry.server.config.ts` is a no-op — the Node
 * SDK's OTel auto-instrumentation throws under workerd), so a root render/SSR
 * failure would otherwise reach NO tracker. `RouteError` is a Client Component
 * whose `useEffect` feeds the owned `js_error` beacon (events_raw, rule 34 —
 * edge, never a Cloud Run route), so even a top-level crash is queryable and
 * joinable. We reuse it (rather than re-implement the UI) so there is one
 * recovery surface and one report path.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="en">
      <body>
        <RouteError
          error={error}
          reset={reset}
          boundary="global_error"
          message="We hit an unexpected error loading the page. Please try again."
        />
      </body>
    </html>
  );
}
